import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockInvoiceService } from '@/services/mock/MockInvoiceService';
import { InvoiceRecord, PaymentReadinessStatus } from '@/types/domain/invoice-management';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Save, CreditCard, Calendar, FileText, IndianRupee } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { formatInr } from '@/lib/utils';

export const InvoiceEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<InvoiceRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form fields
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<PaymentReadinessStatus>('NOT_READY');
  const [subtotal, setSubtotal] = useState<number>(0);
  const [taxAmount, setTaxAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);

  useEffect(() => {
    const loadInvoice = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const data = await mockInvoiceService.getInvoiceById(id);
        if (!data) {
          navigate(ROUTES.INVOICES.LIST);
          return;
        }
        setInvoice(data);
        setVendorInvoiceNumber(data.vendorInvoiceNumber || '');
        setDueDate(data.dueDate ? data.dueDate.slice(0, 10) : '');
        setPaymentTerms(data.paymentTerms || 'Net 30');
        setPaymentStatus(data.paymentStatus || 'NOT_READY');
        setSubtotal(data.subtotal || 0);
        setTaxAmount(data.taxAmount || 0);
        setTotalAmount(data.totalAmount || 0);
      } finally {
        setIsLoading(false);
      }
    };

    loadInvoice();
  }, [id, navigate]);

  const handleSubtotalChange = (newSubtotal: number) => {
    setSubtotal(newSubtotal);
    setTotalAmount(newSubtotal + taxAmount);
  };

  const handleTaxChange = (newTax: number) => {
    setTaxAmount(newTax);
    setTotalAmount(subtotal + newTax);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !invoice) return;

    try {
      setIsSaving(true);
      await mockInvoiceService.updateInvoice(id, {
        vendorInvoiceNumber,
        dueDate: dueDate ? new Date(dueDate).toISOString() : invoice.dueDate,
        paymentTerms,
        paymentStatus,
        subtotal,
        taxAmount,
        totalAmount,
      });
      navigate(ROUTES.INVOICES.DETAILS(id));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update invoice.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Invoice Editor...
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-16 text-center space-y-4 font-poppins">
        <h2 className="text-lg font-bold text-foreground">Invoice Not Found</h2>
        <Button size="sm" onClick={() => navigate(ROUTES.INVOICES.LIST)} className="font-poppins cursor-pointer">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Invoices
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 font-poppins max-w-5xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.INVOICES.DETAILS(id!))}
            className="rounded-xl border-border hover:bg-muted font-poppins cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-primary px-2.5 py-0.5 bg-primary/10 rounded-lg border border-primary/20">
                {invoice.invoiceNumber}
              </span>
              <h1 className="text-xl font-extrabold text-foreground tracking-tight font-poppins">
                Edit Accounts Payable Invoice
              </h1>
            </div>
            <p className="text-xs text-foreground-secondary font-medium mt-0.5 font-poppins">
              Supplier: <strong className="text-foreground">{invoice.vendorName}</strong> • PO Reference: <strong className="text-primary font-mono">{invoice.poNumber}</strong>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.INVOICES.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-invoice-form"
            size="sm"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </div>

      <form id="edit-invoice-form" onSubmit={handleSave} className="space-y-6">
        {/* Section 1: Vendor Invoice Information & Due Dates */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <FileText className="h-4 w-4 text-primary" />
              Vendor Invoice Metadata & Payment Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Vendor Invoice Reference # *
                </label>
                <input
                  type="text"
                  value={vendorInvoiceNumber}
                  onChange={(e) => setVendorInvoiceNumber(e.target.value)}
                  required
                  placeholder="e.g. VND-INV-10001"
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-primary" /> Net Payment Due Date *
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Commercial Payment Terms *
                </label>
                <select
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="Net 30">Net 30 Days</option>
                  <option value="Net 45">Net 45 Days</option>
                  <option value="Net 60">Net 60 Days</option>
                  <option value="2% 10 Net 30">2% 10 Net 30 (Cash Discount)</option>
                  <option value="Immediate">Immediate / Advance</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <CreditCard className="h-3.5 w-3.5 text-primary" /> AP Payment Readiness State *
                </label>
                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as PaymentReadinessStatus)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="NOT_READY">Not Ready — Pending 3-Way Match Clearance</option>
                  <option value="READY_FOR_PAYMENT">Ready for Payment — Verified & Sanctioned</option>
                  <option value="SCHEDULED">Scheduled in Treasury Run</option>
                  <option value="PAID">Paid — Remittance Completed</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Financial Reconciliation (INR ₹) */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <IndianRupee className="h-4 w-4 text-primary" />
              Financial Billed Amounts (INR ₹)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Base Subtotal Amount (₹) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={subtotal}
                  onChange={(e) => handleSubtotalChange(parseFloat(e.target.value) || 0)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-mono font-bold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Statutory GST / Tax Amount (₹) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={taxAmount}
                  onChange={(e) => handleTaxChange(parseFloat(e.target.value) || 0)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-mono font-bold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Total Billed Amount (₹)
                </label>
                <div className="w-full rounded-xl border border-border bg-muted/30 p-2.5 text-xs font-mono font-extrabold text-primary">
                  {formatInr(totalAmount)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Controls */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.INVOICES.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer px-6 shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};
